// First-touch attribution + the event beacon (see worker/track.js for the
// server side). Privacy shape: no cookies, no IDs, no per-user profiles — just
// "this view/click came from channel X". `?me=1` permanently opts a device out
// (the founder visits his own site constantly; without this his traffic
// pollutes every number).
//
// Every event is ALSO mirrored to PostHog (src/posthog.ts, same privacy
// shape) so funnels/retention live there while /ops keeps its own numbers.

import { loadPostHog, phCapture } from './posthog';

export type Attribution = { src: string | null; ref: string | null; landing: string; ts: number };

const ME_KEY = 'cr_me';
const ATTR_KEY = 'cr_attr';

// localStorage can throw under strict privacy settings — tracking just goes quiet.
function safeGet(k: string): string | null {
  try { return localStorage.getItem(k); } catch { return null; }
}
function safeSet(k: string, v: string) {
  try { localStorage.setItem(k, v); } catch { /* no storage, no tracking */ }
}

// Excluded devices (the founder's PC/phone/laptop): flag lives in BOTH
// localStorage and a 2-year cookie so it survives either store being cleared.
// /notme is the self-service page; ?me=1 still works for old links.
const ME_COOKIE_RE = /(?:^|;\s*)cr_me=1(?:;|$)/;
function setMeCookie(on: boolean) {
  try {
    document.cookie = on
      ? 'cr_me=1; max-age=63072000; path=/; SameSite=Lax'
      : 'cr_me=; max-age=0; path=/; SameSite=Lax';
  } catch { /* cookies blocked — localStorage still carries the flag */ }
}

export function isExcluded(): boolean {
  let cookie = false;
  try { cookie = ME_COOKIE_RE.test(document.cookie); } catch { /* ignore */ }
  return safeGet(ME_KEY) === '1' || cookie;
}

/** /notme toggles this. Re-syncs both stores so they can heal each other. */
export function setExcluded(on: boolean) {
  try { on ? localStorage.setItem(ME_KEY, '1') : localStorage.removeItem(ME_KEY); } catch { /* no storage */ }
  setMeCookie(on);
}

const isMe = isExcluded;

// Run once on boot, BEFORE the first event fires: claim ?me=1, then store the
// first touch if this device doesn't have one yet. First touch wins — a return
// visit straight from Google keeps its original channel.
export function initTracking() {
  const qs = new URLSearchParams(window.location.search);
  // /notme must exclude BEFORE the boot page_view fires (TrackManager's effect
  // runs before the page component's), or the first visit still counts itself.
  if (qs.get('me') === '1' || window.location.pathname === '/notme') setExcluded(true);
  if (isMe()) return; // opted-out devices never even load the PostHog bundle
  loadPostHog();
  if (!safeGet(ATTR_KEY)) {
    // ?src= (our minted /go/ links) wins; utm_source folds in as the channel
    // for links we don't control (founder decision 2026-07-26).
    const src = (qs.get('src') || qs.get('utm_source') || '').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 32);
    const attr: Attribution = {
      src: src || null,
      ref: document.referrer ? document.referrer.slice(0, 200) : null,
      landing: window.location.pathname,
      ts: Date.now(),
    };
    safeSet(ATTR_KEY, JSON.stringify(attr));
  }
}

export function getAttribution(): Attribution | null {
  const raw = safeGet(ATTR_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as Attribution; } catch { return null; }
}

const refHost = () => {
  try { return document.referrer ? new URL(document.referrer).hostname : null; } catch { return null; }
};

// Fire-and-forget. sendBeacon survives navigation (checkout/download leave the
// page immediately); keepalive fetch is the fallback.
export function track(t: 'page_view' | 'docs_view' | 'download_click' | 'buy_click' | 'checkout_start', label?: string) {
  if (isMe()) return;
  const props = {
    path: window.location.pathname,
    src: getAttribution()?.src ?? null,
    ref: refHost(),
    label: label ?? null,
  };
  // Mirror to PostHog: both view kinds become $pageview (the path already
  // distinguishes /docs) so PostHog's web-analytics views work out of the box;
  // funnel clicks keep their names.
  phCapture(t === 'page_view' || t === 'docs_view' ? '$pageview' : t, props);
  const body = JSON.stringify({ t, ...props });
  try {
    if (!navigator.sendBeacon('/e', body)) {
      fetch('/e', { method: 'POST', body, keepalive: true }).catch(() => {});
    }
  } catch { /* never let tracking break the page */ }
}
